import axios from 'axios';
import appInfo from '../appInfo';
import request, { prefixSecondPath } from '../request';

let isDisableLocale = false; // 禁用多语言

export interface Labels {
  [key: string]: string;
}

export interface Locale {
  /* 已加载的模块多语言 */
  loadedModules: string[];
  /* 已加载的租户多语言 */
  loadedTenantVersions: string[];
  /* 多语言标签 */
  labels: Labels;
  /* 租户多语言标签 */
  tenatLabels: Labels;
}

export function setDisableLocale (disable = true) {
  isDisableLocale = disable;
}

export function getDisableLocale () {
  return isDisableLocale;
}

const locale: Locale = {
  loadedModules: [],
  loadedTenantVersions: [],
  labels: {},
  tenatLabels: {},
};

export function getLabel(id: string, defaultLabel: string, data?: string[]) {
  let label = locale.tenatLabels[id] || locale.labels[id] || '';
  // 没有值的情况下， 且中文模式下 显示默认补值
  if (!label && (window?.TEAMS?.locale?.lang || 'zh-CN').toLocaleLowerCase().indexOf('zh') > -1) {
    label = defaultLabel || '';
  }

  if (label && /{\d+}/.test(label)) {
    if (Array.isArray(data)) {
      return label.replace(/{\d+}/g, m =>
        data[Number(m.replace(/{|}/g, () => ''))] || '')
    }
  }
  return label;
}

const loadingModules: string[] = [];

export const getLocale = (app?: string) => new Promise((resolve: (reason?: any) => void, reject: (reason?: any) => void) => {
  if (app === '@weapp/vendor' || app === '@weapp/utils' || app === '@weapp/ckeditor4') {
    reject(new Error(`No Locale for ${app}`));
    return;
  }
  if (getDisableLocale() || !app) {
    resolve(locale);
    return;
  }
  const closeLocale: boolean = false;
  const {
    name,
    publicUrl,
  } = appInfo(app);

  const publicDomain = window['publicDomainfileLocale'] || ''; // 走独立文件服务二级域名变量

  const { loadedModules, loadedTenantVersions } = locale;
  if (loadedModules.includes(name) || closeLocale) {
    resolve(locale);
    return;
  }
  /*
  * 登录后获取
  * @verison 版本号，默认 0
  * @lang 当前语言，默认 zh-CN
  * @tenant 租户号
  * @tenantVersion 组合增量修改版本号，默认 0
  */
  const lang: string = ((window.TEAMS?.locale?.lang) || document.cookie.match(/langType=(.*?)(;|$)/)?.[1] || window.navigator.language || 'zh-CN').replace(/-/g, () => '_');
  const version: string = (window.TEAMS?.locale?.version) || '';
  const tenant: string = (window.TEAMS?.currentTenant?.tenantKey) || '';
  const tenantVersion: string = (window.TEAMS?.locale?.[`version_${tenant}`]) || '';
  const promises: Promise<any>[] = [];

  // 是否开启翻译模式 默认 0 不开启 有值情况下为字符串
  const enableTrans = window?.sessionStorage.getItem("enableTrans");  // '0' '1'

  // 开启翻译模式, getItem 有值的情况下,携带对应请求头
  if (enableTrans) {
    axios.interceptors.request.use((config) => {
      config.headers.enableTrans = enableTrans
      return config;
    })
  }

  // 判断是否为翻译模式
  const isOpenTrans = enableTrans && parseInt(enableTrans);
  // 翻译模式下 请求接口, 非翻译模式请求json
  const url: string = isOpenTrans ? prefixSecondPath(`/api/bs/i18n/transmod/getLabels?module=${name}`) : `${publicDomain}${publicUrl}/static/public/resource/i18n/${lang}/${name}.json?v=${version}`;
  promises.push(axios({ url }));

  if (name !== 'ui' && !loadedModules.includes('ui') && !loadingModules.includes('ui')) {
    const uiUrl: string = isOpenTrans ? prefixSecondPath(`/api/bs/i18n/transmod/getLabels?module=ui`) : `${publicDomain}${publicUrl}/static/public/resource/i18n/${lang}/ui.json?v=${version}`;
    promises.push(axios({ url: uiUrl }));
    loadingModules.push('ui');
  }

  const loadTenantVersion = isOpenTrans ? `version_${tenant}` : `version_${tenant}_${tenantVersion}`;
  const hasFetchTenant = loadedTenantVersions.includes(loadTenantVersion) || loadingModules.includes(loadTenantVersion);
  if (!hasFetchTenant) {
    // 翻译模式下
    if (isOpenTrans) {
      // 避免重复请求
      const tenantUrlTrans: string = prefixSecondPath(`/api/bs/i18n/transmod/getLabels?module=${tenant}`);
      promises.push(axios({ url: tenantUrlTrans }));
      loadingModules.push(loadTenantVersion);
    } else if (tenantVersion) { // 非翻译模式 并且存在tenantVersion
      // 不能判断 loadedModules.includes(tenant) 要一直加载最新的覆盖其他的，corsImport 也要重新覆盖
      const tenantUrl: string = `${publicDomain}${publicUrl}/static/public/resource/i18n/${lang}/${tenant}.json?v=${tenantVersion}`;
      promises.push(axios({ url: tenantUrl }));
      loadingModules.push(loadTenantVersion);
    }
  }

  Promise.allSettled(promises).then(([resApp, resND, resRD]) => {
    function results(res: PromiseSettledResult<any>, isTenant?: boolean) {
      if (res && res.status === 'fulfilled') {
        Object.assign(isTenant ? locale.tenatLabels : locale.labels, res.value.data);
        const getUrl: string = res.value.config.url;
        // 模块名称不为ui 并且满足对应的模块.json文件 或者请求模块接口
        if (name !== 'ui' && (new RegExp(`\\/${name}.json`).test(getUrl) || new RegExp(`module=${name}$`).test(getUrl))) {
          locale.loadedModules.push(name);
        }
        // 满足静态资源ui.json 或者满足翻译模式下 参数module=ui 添加入locale模块
        if (/\/ui\.json/.test(getUrl) || /module=ui$/.test(getUrl)) {
          locale.loadedModules.push('ui');
        }
        // 满足租户.json 或者满足翻译模式下 参数module=tenant
        if (new RegExp(`\\/${tenant}.json`).test(getUrl) || new RegExp(`module=${tenant}$`).test(getUrl)) {
          locale.loadedTenantVersions.push(loadTenantVersion);
        }
      }
      if (resND && resND.status !== 'fulfilled') {
        // 如果ui加载失败，清除加载中状态
        const index = loadingModules.findIndex(module => module === 'ui');
        if (index !== -1) {
          loadingModules.splice(index, 1);
        }
      }
      if (resRD && resRD.status !== 'fulfilled') {
        // 如果租户加载失败，清除加载中状态
        const index = loadingModules.findIndex(module => module === loadTenantVersion);
        if (index !== -1) {
          loadingModules.splice(index, 1);
        }
      }
    }

    results(resApp);
    results(resND);
    results(resRD, true);
    lang !== "zh_CN" && resolve(locale);
  }).catch(err => {
    lang !== "zh_CN" && resolve(locale);
  });

  if (lang === "zh_CN") {
    resolve(locale);
  }
})

//千分位正向转换方法
export function getLocaleThousands(number: string): string {
  //安全校验,匹配基本数字
  if (number === '') {
    return ''
  }

  const regex = /^-?\d*(\.\d*)?$/
  const isMatched = regex.test(number);

  if (!isMatched) {
    return getLabel('227868', '非标准格式数据')
  }

  //判断是否为负数并去除负号
  const Negativeregex = /-/
  let isNegative = Negativeregex.test(number);

  //从window中获取千分位设置
  const type: number = window?.ECODE?.locale?.numberformat || window?.ECODE?.locale?.tnumberformat || 1

  //正向转换算法
  const range = 3
  let str = number.toString();
  str = str.replace(/-/g, '')
  const [left, right] = str.split('.');

  const strArrint = left.split('').reverse();
  let strArrdecimal: string[] = []

  let strArrintresult: Array<any> = [];
  let strArrdecimalresult: Array<any> = [];
  //分割number
  for (let i = 0; i < strArrint.length; i += range) {
    strArrintresult.push(strArrint.slice(i, i + range).reverse().join(''))
  }
  if (right) {
    strArrdecimal = right.split('')
    for (let i = 0; i < strArrdecimal.length; i += range) {
      strArrdecimalresult.push(strArrdecimal.slice(i, i + range).join(''))
    }
  }
  let returnNum: string = ''
  switch (type) {
    case 1:
      // DEFAULT
      returnNum = `${strArrintresult.reverse().join(',')}${right ? ('.' + right) : ''}`
      break
    case 2:
      //FRANCE
      returnNum = `${strArrintresult.reverse().join(' ')}${right ? ',' : ''}${strArrdecimalresult.join(' ')}`
      break
    case 3:
      //GERMAN
      returnNum = `${strArrintresult.reverse().join('.')}${right ? ',' : ''}${strArrdecimalresult.join('.')}`
      break
    case 4:
      // US
      returnNum = `${strArrintresult.reverse().join(',')}${right ? '.' : ''}${strArrdecimalresult}`
      break
  }
  returnNum = isNegative ? '-' + returnNum : returnNum

  return returnNum
}
//千分位逆向转换方法
export function getUnifiedNumber(number: string): string {
  //从window中获取千分位设置
  const type: number = window?.ECODE?.locale?.numberformat || window?.ECODE?.locale?.tnumberformat || 1

  //逆向转换算法
  let unifiedNumber: string = ''
  switch (type) {
    case 1:
      // US -- CN
      unifiedNumber = number?.replace(/,/g, '')
      break
    case 2:
      //FRANCE
      unifiedNumber = number?.replace(/\s/g, '').replace(/,/g, '.')
      break
    case 3:
      //GERMAN
      unifiedNumber = number?.replace(/\./g, '').replace(/,/g, '.')
      break
    case 4:
      // US -- CN
      unifiedNumber = number?.replace(/,/g, '')
      break
  }

  //安全校验,匹配基本数字
  const regex = /^-?\d+(\.\d*)?$/
  const isMatched = regex.test(unifiedNumber);

  if (unifiedNumber === '') {
    return ''
  }
  else if (!isMatched) {
    return getLabel('227868', '非标准格式数据')
  }
  else {
    return unifiedNumber
  }
}

export function getMoney(param: string) {

  let languageid = ((window?.TEAMS?.locale?.lang) || document.cookie.match(/langType=(.*?)(;|$)/)?.[1] || window.localStorage.getItem('langType') || window.navigator.language || 'zh-CN').replace(/-/g, () => '_')
  let amount = param
  const amtinwords = window?.cuslocale?.amtinwords
  if (amtinwords) {
    return transToChinese(amount)
  }
  else {
    if (languageid == 'zh_CN' || languageid == 'zh_TW') {
      return transToChinese(amount)
    }
    else {
      return transToEnglish(amount)
    }
  }
}


/**
* 将数字金额转换为中文大写
* @param num 数字金额
* @param type 转换类型，0：金额整数部分为零时是否保留零，1：金额整数部分为零时省略零
* @returns 中文大写金额字符串
*/
function transToChinese(num: string) {

  //判断是否为负数并去除负号
  const Negativeregex = /-/
  let isNegative = Negativeregex.test(num);
  num = num.replace(/-/g, '')

  let money = Number(num)

  let cnNums = [
    "零",
    "壹",
    "贰",
    "叁",
    "肆",
    "伍",
    "陆",
    "柒",
    "捌",
    "玖"
  ] //汉字的数字
  let cnIntRadice = ["", "拾", "佰", "仟"] //基本单位
  let cnIntUnits = ["", "万", "亿", "兆"] //对应整数部分扩展单位
  let cnDecUnits = ["角", "分", "毫", "厘"] //对应小数部分单位
  let cnIntLast = "元" //整型完以后的单位
  let IntegerNum: string = "" //金额整数部分
  let DecimalNum: string = "" //金额小数部分
  let ChineseStr = "" //输出的中文金额字符串
  let parts: string[] = ["", ""] //分离金额后用的数组，预定义
  let zeroCount, IntLen, n, p, q, m, decLen
  if (money == undefined || Number.isNaN(money)) {
    return ""
  }
  if (money == 0) {
    ChineseStr = cnNums[0] + "元整"
    return ChineseStr
  }
  if (money.toString().indexOf(".") == -1) {
    IntegerNum = money.toString()
    DecimalNum = ""
    cnIntLast = "元整"
  } else {
    parts = money.toString().split(".")
    IntegerNum = parts[0]
    DecimalNum = parts[1].substr(0, 2)
  }
  if (parseInt(IntegerNum, 10) > 0) {
    //获取整型部分转换
    zeroCount = 0
    IntLen = IntegerNum.length
    for (let i = 0; i < IntLen; i++) {
      n = IntegerNum.substr(i, 1)
      p = IntLen - i - 1
      q = p / 4
      m = p % 4
      if (n == "0") {
        zeroCount++
      } else {
        if (zeroCount > 0) {
          ChineseStr += cnNums[0]
        }
        zeroCount = 0 //归零
        ChineseStr += cnNums[parseInt(n)] + cnIntRadice[m]
      }
      if (m == 0 && zeroCount < 4) {
        ChineseStr += cnIntUnits[q]
      }
    }
    ChineseStr += cnIntLast
    //整型部分处理完毕
  }
  if (DecimalNum != "") {
    //小数部分
    decLen = DecimalNum.length
    n = DecimalNum.substr(0, 1)
    if (decLen == 2) {
      let n2 = DecimalNum.substr(1, 1)
      if (n == "0") {
        ChineseStr += cnNums[Number(n)] + cnNums[Number(n2)] + cnDecUnits[1]
      }
      else {
        ChineseStr += cnNums[Number(n)] + cnDecUnits[0] + cnNums[Number(n2)] + cnDecUnits[1]
      }
    }
    else {
      ChineseStr += cnNums[Number(n)] + cnDecUnits[0]
    }

  }
  if (ChineseStr == "") {
    ChineseStr += cnNums[0] + cnIntLast
  }

  ChineseStr = isNegative ? '负' + ChineseStr : ChineseStr
  return ChineseStr
}

//翻译成英文，整数数字
function transToEnglish(number: string): string {
  //判断是否为负数并去除负号
  const Negativeregex = /-/
  let isNegative = Negativeregex.test(number);
  number = number.replace(/-/g, '')

  let amount = Number(number)
  //翻译成英文数字需要的文本
  const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE', 'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
  const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

  const numToWords = (num: number): string => {
    if (num === 0) return 'ZERO';
    if (num < 20) return ones[num];
    if (num < 100) return (num % 10 == 0) ? tens[Math.floor(num / 10)] : (tens[Math.floor(num / 10)] + '-' + ones[num % 10]);
    if (num < 1000) return (num % 100 == 0) ? ones[Math.floor(num / 100)] + ' HUNDRED' : ones[Math.floor(num / 100)] + ' HUNDRED' + ' AND ' + numToWords(num % 100);
    if (num < 1000000) return (num % 1000 == 0) ? numToWords(Math.floor(num / 1000)) + ' THOUSAND' : numToWords(Math.floor(num / 1000)) + ' THOUSAND' + ',' + numToWords(num % 1000);
    if (num < 1000000000) return (num % 1000000 == 0) ? numToWords(Math.floor(num / 1000000)) + ' MILLION' : numToWords(Math.floor(num / 1000000)) + ' MILLION' + ',' + numToWords(num % 1000000);
    return (num % 1000000000 == 0) ? numToWords(Math.floor(num / 1000000000)) + ' BILLION' : numToWords(Math.floor(num / 1000000000)) + ' BILLION' + ',' + numToWords(num % 1000000000);
  };

  const convertCentsToWords = (cents: number): string => {
    if (cents === 0) return 'ZERO';
    return numToWords(cents) + ' CENTS';
  };

  const dollars = Math.floor(amount);
  const cents = Math.round((amount - dollars) * 100);

  const dollarsInWords = numToWords(dollars) + ' DOLLARS';
  const centsInWords = convertCentsToWords(cents);

  return isNegative ?
    ('Negative ' + dollarsInWords + (cents ? ' AND ' + centsInWords + ' ONLY' : ' ONLY')) :
    (dollarsInWords + (cents ? ' AND ' + centsInWords + ' ONLY' : ' ONLY'));
}


//传参给币符，返回币种名称
export function getCurrencyName(currency: string): any {
  let currencyMap: any = window?.ECODE?.locale?.currencymap ? window?.ECODE?.locale?.currencymap : window?.currencymap
  let chineseCurrency: any = ''

  chineseCurrency = currencyMap?.[currency?.toUpperCase()]
  return chineseCurrency ? chineseCurrency : currency
}

declare global {
  interface Window { currencymap: any; }
}


//匿名自调用函数，查看window.TEAMS上是否有locale属性，以此来判断是否是免登模式
(function () {
  if (!window?.TEAMS?.locale || !window?.ECODE?.locale?.currencymap) {
    try {
      //请求币种转换Map
      request({
        url: `/papi/i18n/numberformat/symbol2name`,
        method: 'post',
        //免登模式下优先使用window中的租户key，若没有则请求国际化的租户key获取保底currencymap
        data: { 'tenantKey': window?.TEAMS?.currentTenant?.tenantKey ? window?.TEAMS?.currentTenant?.tenantKey : '' },
      })
        .then((res: any) => {
          window.currencymap = res?.status ? res?.data : {}
        })
        .catch(error => {
          window.currencymap = {}
        });
    }
    catch (error) {
      window.currencymap = {}
    }
  }
}())

//传参给两种币符,日期,汇率类型
export const getExchangeConverter = async (rateSymbol: string, targetSymbol: string, type: string, date?: string) => {
  if (type == '1') {
    let nowdate = new Date(),
      year = nowdate.getFullYear(), //获取完整的年份(4位)
      month = nowdate.getMonth() + 1, //获取当前月份(0-11,0代表1月)
      date1 = nowdate.getDate(), // 获取当前日(1-31)
      strmonth = '',
      strDate = date1.toString()
    if (month < 10) strmonth = `0${month}`// 如果月份是个位数，在前面补0
    if (date1 < 10) strDate = `0${date1}` // 如果日是个位数，在前面补0

    date = `${year}-${strmonth}-${strDate}`
  }

  try {
    const response = await request({
      url: `/papi/i18n/rate/getTargetRate`,
      method: 'post',
      //免登模式下使用window中的租户key
      data: { rateSymbol, targetSymbol, date, type, 'tenantKey': window?.TEAMS?.currentTenant?.tenantKey },
    })
    if (response?.data?.rate) {
      return Promise.resolve(response?.data?.rate)
    } else {
      return Promise.reject(response?.data?.msg)
    }
  } catch (error) {
    return Promise.reject(error)
  }
}
/**
 * 获取当前系统使用的语言 https://weapp.mulinquan.cn/api/bs/i18n/languagemanage/getSys
 * 
 * 语言说明：
 * zh_CN: 简体中文
 * en_US: 英语
 * zh_TW: 繁体中文 
 * ko_KR: 韩语
 * ja_JP: 日语
 * de_DE: 德语
 * fr_FR: 法语
 * pt_PT: 葡萄牙语
 * ar_AE: 阿拉伯语
 * ru_RU: 俄语
 * th_TH: 泰语
 * es_ES: 西班牙语
 */
export const getLang = () => ((window.TEAMS?.locale?.lang) || document.cookie.match(/langType=(.*?)(;|$)/)?.[1] || window.navigator.language || 'zh-CN').replace(/-/g, () => '_');
